import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ProducerSettings {
  id: string;
  user_id: string;
  // Notification Settings
  notify_new_assignment: boolean;
  notify_updated_assignment: boolean;
  notify_deleted_assignment: boolean;
  notify_starting_soon: boolean;
  notify_announcements: boolean;
  notify_location_alerts: boolean;
  notify_weather: boolean;
  reminder_minutes: number;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  // Location Tracking Settings
  location_tracking_enabled: boolean;
  location_tracking_during_assignments_only: boolean;
  tracking_frequency: "low" | "standard" | "high";
  tracking_accuracy: "low" | "medium" | "high";
  highlight_crew_far_from_stage: boolean;
  // Optional in the type because supabase-generated types may lag
  // the migration that added show_gps_corrections. Runtime default true.
  show_gps_corrections?: boolean;
  // PDF Export Settings (removed: pdf_include_logo, pdf_include_notes)
  pdf_time_format: "24h" | "12h";
  pdf_default_view: "list" | "timeline";
  pdf_filename_structure: "festival_date" | "crew_name" | "festival_day_view";
  // Schedule Settings (removed: schedule_default_zoom)
  schedule_time_format: "24h" | "12h";
  schedule_show_current_time_marker: boolean;
  schedule_auto_scroll_current_time: boolean;
  default_assignment_length: number; // 15, 30, 45, or 60 minutes
  // Offline Sync Settings
  offline_sync_interval: number;
  offline_download_lineup: boolean;
  offline_download_assignments: boolean;
  offline_cache_maps: boolean;
  created_at: string;
  updated_at: string;
}

const defaultSettings: Omit<ProducerSettings, "id" | "user_id" | "created_at" | "updated_at"> = {
  notify_new_assignment: true,
  notify_updated_assignment: true,
  notify_deleted_assignment: true,
  notify_starting_soon: true,
  notify_announcements: true,
  notify_location_alerts: true,
  notify_weather: true,
  reminder_minutes: 15,
  quiet_hours_enabled: false,
  quiet_hours_start: "22:00",
  quiet_hours_end: "07:00",
  location_tracking_enabled: true,
  location_tracking_during_assignments_only: false,
  tracking_frequency: "standard",
  tracking_accuracy: "medium",
  highlight_crew_far_from_stage: true,
  show_gps_corrections: true,
  pdf_time_format: "24h",
  pdf_default_view: "list",
  pdf_filename_structure: "festival_date",
  schedule_time_format: "24h",
  schedule_show_current_time_marker: true,
  schedule_auto_scroll_current_time: true,
  default_assignment_length: 15,
  offline_sync_interval: 5,
  offline_download_lineup: true,
  offline_download_assignments: true,
  offline_cache_maps: true,
};

export function useProducerSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: settings,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["producer-settings", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("producer_settings")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code === "PGRST116") {
        // No settings found, create default
        const { data: newData, error: insertError } = await supabase
          .from("producer_settings")
          .insert({ user_id: user.id, ...defaultSettings })
          .select()
          .single();

        if (insertError) throw insertError;
        return newData as ProducerSettings;
      }

      if (error) throw error;
      return data as ProducerSettings;
    },
    enabled: !!user?.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<ProducerSettings>) => {
      if (!user?.id) throw new Error("No user");

      const { error } = await supabase
        .from("producer_settings")
        .update(updates)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-settings", user?.id] });
      // Removed success toast as per user request
    },
    onError: () => {
      // Settings save failed - no action needed, user sees stale state
    },
  });

  return {
    settings: settings ?? (defaultSettings as ProducerSettings),
    isLoading,
    error,
    updateSettings: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
